import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BRAND = '/brand';

// Helper: copy-to-clipboard for color HEX
const CopyHex = ({ hex }) => {
  const [done, setDone] = React.useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard?.writeText(hex); setDone(true); setTimeout(() => setDone(false), 1200); }}
      className="text-[11px] font-mono tracking-wide text-text-muted hover:text-gold transition-colors"
      data-testid={`copy-${hex.replace('#','')}`}
    >
      {done ? '✓ copied' : hex}
    </button>
  );
};

const Swatch = ({ name, hex, sub }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/[0.025] border border-gold/15 p-3">
    <div className="w-14 h-14 rounded-lg shrink-0 border border-gold/20" style={{ backgroundColor: hex }} />
    <div className="min-w-0">
      <p className="text-sm text-text-main font-medium">{name}</p>
      <CopyHex hex={hex} />
      {sub && <p className="text-[11px] text-text-muted mt-0.5">{sub}</p>}
    </div>
  </div>
);

const Card = ({ title, children, testid }) => (
  <section className="rounded-2xl bg-white/[0.025] border border-gold/20 p-5 sm:p-6" data-testid={testid}>
    <h2 className="font-heading text-xl sm:text-2xl text-gold-gradient mb-4 pb-2 border-b border-gold/20">{title}</h2>
    {children}
  </section>
);

const AssetTile = ({ src, label, dark, testid }) => (
  <a
    href={src}
    download
    className="block group rounded-xl bg-white/[0.02] border border-gold/15 hover:border-gold/50 p-3 transition-colors"
    data-testid={testid}
  >
    <div className={`aspect-square rounded-lg overflow-hidden flex items-center justify-center ${dark ? 'bg-[#0f0508]' : 'bg-white'}`}>
      <img src={src} alt={label} className="max-w-[85%] max-h-[85%] object-contain" loading="lazy" />
    </div>
    <p className="text-[12px] text-text-main mt-2 text-center group-hover:text-gold transition-colors">{label}</p>
    <p className="text-[10px] text-gold/60 text-center">⬇ Download</p>
  </a>
);

const RuleRow = ({ rule, ok }) => (
  <li className="flex items-start gap-2 text-[13px] text-text-main">
    <span className={ok ? 'text-emerald-400' : 'text-rose-400'}>{ok ? '✓' : '✗'}</span>
    <span>{rule}</span>
  </li>
);

export default function BrandPage() {
  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="brand-page">
      <SEO
        title="ViratAstro Brand Guidelines | Logo, Colors, Typography & Assets"
        description="Official ViratAstro brand guidelines — VA monogram logo files (SVG/PNG), favicon package, color palette (HEX codes), typography, usage rules, and downloadable variants."
        path="/brand"
        image="/brand/va-horizontal-dark.png"
        keywords="viratastro brand guidelines, va logo, vivek mudgal logo, astrology brand kit, brand identity"
      />
      <Header />

      {/* Print button — visible only on screen */}
      <style>{`
        @media print {
          header, footer, .no-print { display: none !important; }
          body { background: #fff !important; color: #000 !important; }
          .brand-card { background: #fff !important; border-color: #ccc !important; color: #000 !important; }
        }
      `}</style>

      <section className="pt-28 pb-16">
        <div className="container-custom max-w-6xl">

          {/* Hero */}
          <div className="text-center mb-10">
            <Link to="/" className="inline-flex items-center gap-1.5 text-text-muted hover:text-gold text-sm mb-4">← होम</Link>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-gold-gradient mb-3" data-testid="brand-hero-title">ViratAstro Brand Kit</h1>
            <p className="text-text-muted text-base sm:text-lg">Logo files · Favicons · Colours · Typography · Usage rules</p>
            <button
              onClick={() => window.print()}
              className="no-print mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-full border border-gold/40 text-gold text-sm hover:bg-gold/10 transition-colors"
              data-testid="brand-print-btn"
            >
              📄 Print / Save as PDF
            </button>
          </div>

          <div className="space-y-6">

            {/* PRIMARY LOGO */}
            <Card title="🪔 Primary Logo (EXACT Cosmic VA — User-Supplied Master)" testid="card-primary">
              <div className="grid sm:grid-cols-2 gap-4 mb-3">
                <AssetTile src={`${BRAND}/va-master-2048.png`} label="Cosmic Master (2048 PNG · highest res)" dark testid="asset-cosmic-2048" />
                <AssetTile src={`${BRAND}/va-original-2048w.png`} label="Original Reference (with text + podium)" dark testid="asset-original" />
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <AssetTile src={`${BRAND}/va-master-1024.png`} label="1024 PNG" dark testid="asset-cosmic-1024" />
                <AssetTile src={`${BRAND}/va-master-1024.webp`} label="1024 WebP (smaller file)" dark testid="asset-cosmic-webp" />
                <AssetTile src={`${BRAND}/va-monogram.svg`} label="SVG Vector Container" dark testid="asset-svg" />
              </div>
              <p className="text-[13px] text-text-muted mt-3">
                <strong className="text-gold">Primary master</strong> for header, social profiles, marketing creatives — this is the EXACT image you supplied, preserved at full fidelity (2048×2048).
                The SVG container embeds the same PNG so it remains losslessly scalable for Illustrator/print workflows.
              </p>
            </Card>

            {/* HORIZONTAL */}
            <Card title="↔ Horizontal Logo" testid="card-horizontal">
              <p className="text-[13px] text-text-muted mb-4">For website headers, email signatures, banners.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <AssetTile src={`${BRAND}/va-horizontal-dark.png`} label="On Dark BG" dark testid="asset-h-dark" />
                <AssetTile src={`${BRAND}/va-horizontal-light.png`} label="On White BG" testid="asset-h-light" />
                <AssetTile src={`${BRAND}/va-horizontal-transparent.png`} label="Transparent" dark testid="asset-h-transparent" />
              </div>
            </Card>

            {/* VERTICAL */}
            <Card title="↕ Vertical Logo" testid="card-vertical">
              <p className="text-[13px] text-text-muted mb-4">For social media profile cards, square slots, business cards.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <AssetTile src={`${BRAND}/va-vertical-dark.png`} label="On Dark BG" dark testid="asset-v-dark" />
                <AssetTile src={`${BRAND}/va-vertical-light.png`} label="On White BG" testid="asset-v-light" />
                <AssetTile src={`${BRAND}/va-vertical-transparent.png`} label="Transparent" dark testid="asset-v-transparent" />
              </div>
            </Card>

            {/* ICON ONLY + STAMP */}
            <Card title="◉ Icon-only & Stamp Variants" testid="card-icon">
              <div className="grid sm:grid-cols-3 gap-4">
                <AssetTile src={`${BRAND}/va-icon-transparent-1024.png`} label="Icon (Transparent)" dark testid="asset-icon-transp" />
                <AssetTile src="/apple-touch-icon.png" label="App Icon (180px)" dark testid="asset-app-icon" />
                <AssetTile src={`${BRAND}/va-stamp-seal.png`} label="Circular Seal (PDF/Reports)" dark testid="asset-stamp" />
              </div>
            </Card>

            {/* MONOCHROME */}
            <Card title="◼ Monochrome Versions" testid="card-mono">
              <p className="text-[13px] text-text-muted mb-4">For print, single-colour applications, embossed/foil stamping.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <AssetTile src={`${BRAND}/va-mono-gold-1024.png`} label="Solid Gold" dark testid="asset-mono-gold" />
                <AssetTile src={`${BRAND}/va-mono-white-1024.png`} label="Pure White" dark testid="asset-mono-white" />
                <AssetTile src={`${BRAND}/va-mono-black-1024.png`} label="Pure Black" testid="asset-mono-black" />
              </div>
            </Card>

            {/* FAVICONS */}
            <Card title="🌐 Favicon Package" testid="card-favicons">
              <p className="text-[13px] text-text-muted mb-4">All sizes installed at site root and referenced in <code className="text-gold">index.html</code> + <code className="text-gold">manifest.json</code>.</p>
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-y-1.5 text-[13px] text-text-main">
                <li>✓ <a className="text-gold hover:underline" href="/favicon.ico" target="_blank" rel="noreferrer">favicon.ico</a> (16/32/48)</li>
                <li>✓ <a className="text-gold hover:underline" href="/favicon-16x16.png" target="_blank" rel="noreferrer">16×16 PNG</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/favicon-32x32.png" target="_blank" rel="noreferrer">32×32 PNG</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/favicon-48x48.png" target="_blank" rel="noreferrer">48×48 PNG</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/favicon-64x64.png" target="_blank" rel="noreferrer">64×64 PNG</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/favicon-96x96.png" target="_blank" rel="noreferrer">96×96 PNG</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/apple-touch-icon.png" target="_blank" rel="noreferrer">180×180 (Apple)</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/android-chrome-192x192.png" target="_blank" rel="noreferrer">192×192 (Android)</a></li>
                <li>✓ <a className="text-gold hover:underline" href="/android-chrome-512x512.png" target="_blank" rel="noreferrer">512×512 (PWA)</a></li>
              </ul>
            </Card>

            {/* COLORS */}
            <Card title="🎨 Brand Colours" testid="card-colors">
              <p className="text-[13px] text-text-muted mb-4">Use the deep navy as primary background. Gold as accent, never as large fill. White text for hierarchy.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Swatch name="Cosmic Navy (Primary BG)" hex="#0f0508" sub="rgb(13, 7, 32)" />
                <Swatch name="Deep Purple (Surface)" hex="#15093A" sub="card surfaces" />
                <Swatch name="Royal Gold (Accent)" hex="#D4AF37" sub="primary CTA / headings" />
                <Swatch name="Bright Gold (Hover/Glow)" hex="#FFC53D" sub="hover / focus" />
                <Swatch name="Soft Gold (Highlight)" hex="#FFE08A" sub="subtle highlights" />
                <Swatch name="Silver (Secondary metal)" hex="#C0C5CE" sub="silver A in monogram" />
                <Swatch name="Text Main" hex="#F4F1E8" sub="body & headings" />
                <Swatch name="Text Muted" hex="#9C9484" sub="secondary copy" />
                <Swatch name="Verdict Success" hex="#34D399" sub="emerald" />
              </div>
            </Card>

            {/* TYPOGRAPHY */}
            <Card title="📝 Typography" testid="card-typography">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl bg-white/[0.02] border border-gold/15 p-4">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gold/80 mb-2">Headings · font-heading</p>
                  <p className="font-heading text-3xl text-text-main">Cinzel · Royal Serif</p>
                  <p className="text-[12px] text-text-muted mt-1">Used for hero titles, card headings, brand wordmark. Weights: 500, 600, 700.</p>
                </div>
                <div className="rounded-xl bg-white/[0.02] border border-gold/15 p-4">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-gold/80 mb-2">Body · font-body</p>
                  <p className="font-body text-base text-text-main">Inter · Modern Sans-Serif</p>
                  <p className="text-[12px] text-text-muted mt-1">Used for body copy, navigation, labels, CTAs. Weights: 400, 500, 600.</p>
                </div>
              </div>
            </Card>

            {/* DO's & DON'TS */}
            <Card title="✅ Do&apos;s &nbsp;·&nbsp; ✗ Don&apos;ts" testid="card-rules">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-bold text-emerald-300 mb-2">DO</h3>
                  <ul className="space-y-1.5">
                    <RuleRow ok rule="Maintain clear-space equal to 25% of icon width around every placement" />
                    <RuleRow ok rule="Use the cosmic master on dark backgrounds (≥ #0f0508 brightness)" />
                    <RuleRow ok rule="Use SVG/mono versions for print, embossing, foil-stamp" />
                    <RuleRow ok rule="Pair with Cinzel headings + Inter body copy" />
                    <RuleRow ok rule="Minimum size: 32px for digital, 12mm for print" />
                  </ul>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-rose-300 mb-2">DON&apos;T</h3>
                  <ul className="space-y-1.5">
                    <RuleRow rule="Don't recolour the cosmic master — use mono variants instead" />
                    <RuleRow rule="Don't stretch, skew, or rotate the monogram" />
                    <RuleRow rule="Don't place on busy backgrounds — use clear-space rule" />
                    <RuleRow rule="Don't add drop-shadows beyond the built-in gold glow" />
                    <RuleRow rule="Don't use the ॐ emoji as a substitute logo" />
                  </ul>
                </div>
              </div>
            </Card>

            {/* CLEAR-SPACE */}
            <Card title="📐 Minimum Size & Clear-Space" testid="card-spacing">
              <div className="grid sm:grid-cols-2 gap-6 items-center">
                <div>
                  <p className="text-[13px] text-text-main mb-3">
                    Reserve <strong className="text-gold">25% of the icon width</strong> as clear-space on every side. No other graphic, text, or page edge may enter this protected zone.
                  </p>
                  <p className="text-[13px] text-text-main">
                    Minimum display sizes:
                  </p>
                  <ul className="mt-2 space-y-1 text-[12px] text-text-muted">
                    <li>• Digital UI: <strong className="text-gold">32 × 32 px</strong></li>
                    <li>• Mobile header: <strong className="text-gold">40 × 40 px</strong></li>
                    <li>• Print: <strong className="text-gold">12 × 12 mm</strong></li>
                    <li>• Stamp / Seal: <strong className="text-gold">25 × 25 mm</strong></li>
                  </ul>
                </div>
                <div className="rounded-xl bg-[#0f0508] border border-gold/20 p-6 flex items-center justify-center">
                  <div className="relative">
                    <div className="absolute -inset-6 border border-dashed border-gold/40 rounded-2xl" />
                    <img src={`${BRAND}/va-icon-transparent-512.png`} alt="Clear-space demo" className="w-32 h-32" />
                  </div>
                </div>
              </div>
            </Card>

            {/* WHERE TO FIND */}
            <Card title="🗂 Asset File Map (developers)" testid="card-filemap">
              <pre className="text-[11px] sm:text-[12px] font-mono text-text-muted bg-[#0f0508] rounded-lg p-4 overflow-x-auto border border-gold/15">{`/public
├── favicon.ico                      multi-res (16/32/48)
├── favicon-{16,32,48,64,96}x{...}.png
├── apple-touch-icon.png             180 × 180
├── android-chrome-192x192.png
├── android-chrome-512x512.png
├── logo.png                         schema.org "logo" (512 cosmic)
├── manifest.json                    PWA install metadata
└── brand/
    ├── va-monogram.svg              VECTOR MASTER
    ├── va-cosmic-1024.png           Cosmic PNG master
    ├── va-icon-transparent-{512,1024}.png
    ├── va-icon-transparent.svg
    ├── va-horizontal-{dark,light,transparent}.png
    ├── va-vertical-{dark,light,transparent}.png
    ├── va-mono-{gold,white,black}.svg + -1024.png
    └── va-stamp-seal.png            circular seal for PDF reports`}</pre>
            </Card>

            <p className="text-center text-[11px] text-text-muted/70 pt-4 pb-2">
              © {new Date().getFullYear()} ViratAstro · Vivek Mudgal. All brand assets are property of ViratAstro.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
